<template>
  <div v-if="!enInter" class="qdh">
    <DetailItem label="Horaires de la formation">
      <RadioGroup
        v-if="isEditing"
        v-model="editedQuote.horaires_nuit"
        :options="horairesOptions"
        vertical
      />
      <span v-else data-testid="quote-detail-horaires">{{ libelleHoraires }}</span>
    </DetailItem>
    <DetailItem
      v-if="afficheGroupesDeNuit"
      label="Nombre de groupes concernés par les horaires de nuit"
    >
      <template v-if="isEditing">
        <Input
          v-model.number="editedQuote.horaires_nuit_nombre_groupes"
          type="number"
          :min="1"
          :max="totalGroupes"
          aria-label="Nombre de groupes concernés par les horaires de nuit"
        />
        <small class="qdh-hint">sur {{ totalGroupes }} groupes du devis</small>
      </template>
      <template v-else>
        <span v-if="quote.horaires_nuit_nombre_groupes">
          {{ quote.horaires_nuit_nombre_groupes }} sur {{ totalGroupes }}
        </span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
  </div>
</template>

<script setup>
/**
 * HORAIRES DE LA FORMATION — en journée, ou avec des horaires de nuit
 * (21h-6h), et combien de groupes sont concernés quand le devis en compte
 * plusieurs (avec un seul, c'est forcément lui).
 *
 * POSÉS DANS « DISPONIBILITÉS DU CLIENT » (retour Figma du 2026-09-11) : ils
 * disent QUAND on intervient, pas où. Ils fermaient auparavant « Lieu de la
 * formation », une section que la fiche ne montre que pour une formation
 * INTRA ou en location — la question était donc introuvable sur une demande
 * INTER ou sans lieu choisi. « Disponibilités » s'affiche pour toute demande.
 *
 * SAUF EN INTER : les horaires sont alors ceux de la session choisie, et la
 * mention imprimée sur le devis n'y aurait pas de sens.
 *
 * PARTAGÉ : la fiche v2, la fiche v1 et le tunnel « Analyse du besoin » (qui
 * monte la fiche v2) le rendent tous. Le libellé vient de
 * `shared/quote-intra-location.js`, comme sur le devis et le PDF.
 */
import { computed } from 'vue'
import { DetailItem, DetailEmpty } from '../data'
import { RadioGroup, Input } from '../ui'
import {
  HORAIRES_FORMATION_OPTIONS as horairesOptions,
  libelleHorairesNuit
} from '@shared/quote-intra-location.js'

const props = defineProps({
  quote: {
    type: Object,
    required: true
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  editedQuote: {
    type: Object,
    default: () => ({})
  }
})

const enInter = computed(() => (
  (props.isEditing ? props.editedQuote?.formation_lieu : props.quote?.formation_lieu) === 'INTER'
))

const totalGroupes = computed(() => (
  Number(props.editedQuote?.nombre_groupes ?? props.quote?.nombre_groupes) || 1
))

const enHorairesDeNuit = computed(() => Boolean(
  props.isEditing ? props.editedQuote?.horaires_nuit : props.quote?.horaires_nuit
))

const afficheGroupesDeNuit = computed(() => enHorairesDeNuit.value && totalGroupes.value > 1)

const libelleHoraires = computed(() => (
  libelleHorairesNuit({
    horairesNuit: props.quote?.horaires_nuit,
    nombreGroupes: totalGroupes.value,
    groupesConcernes: props.quote?.horaires_nuit_nombre_groupes
  }) || 'Formation en journée'
))
</script>

<style scoped>
/* Deux champs côte à côte, comme dans une grille de fiche : la section
   « Disponibilités » n'en a pas (le calendrier y prend toute la largeur). */
.qdh {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: var(--spacing-6);
  margin-top: var(--spacing-md);
}

.qdh-hint {
  color: var(--color-text-tertiary);
  display: block;
  margin-top: 0.25rem;
}

@media (max-width: 768px) {
  .qdh {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
